import { FileText, Paperclip, Send, X } from 'lucide-react';
import { isQuestionAnswerThreadMessage } from '../../utils/questionThreadMessages';
import { resolveQuestionFromMessage } from '../../utils/questionMessage';
import { AttachmentView } from './AttachmentView';
import type { useQuestionThread } from './useQuestionThread';

export type QuestionThreadState = ReturnType<typeof useQuestionThread>;

export function QuestionThreadMessageList({ thread }: { thread: QuestionThreadState }) {
  const { active, threadRef, threadMessages, loading, isCandidateMessage } = thread;

  if (!active || !threadRef) {
    return null;
  }

  return (
    <>
      {loading && threadMessages.length === 0 ? (
        <p className="mh-question-reply-hint">Loading thread…</p>
      ) : null}

      {threadMessages.map((message) => {
        const isSender = isCandidateMessage(message);
        const isAnswer = isQuestionAnswerThreadMessage(
          message,
          threadRef,
          isCandidateMessage,
          threadMessages
        );
        const questionPayload = resolveQuestionFromMessage(message);

        return (
          <div
            key={String(message.id)}
            className={`mh-bubble-row ${isSender ? 'mh-bubble-row--mine' : ''}`}
          >
            <div className={`mh-bubble ${isSender ? 'mh-bubble--mine' : ''}`}>
              {isAnswer ? (
                <span className="mh-bubble-tag">Your answer</span>
              ) : message.kind === 'question' || questionPayload?.questionText ? (
                <span className="mh-bubble-tag">Question</span>
              ) : null}
              {message.attachments?.map((file, index) => (
                <div key={`${file.fileUrl}-${index}`} className="mb-2">
                  <AttachmentView file={file} />
                </div>
              ))}
              {message.content?.trim() ? <p className="mh-bubble-body">{message.content}</p> : null}
              <div className="mh-bubble-meta">
                <span>
                  {new Date(message.createdAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}

export function QuestionThreadComposerBar({ thread }: { thread: QuestionThreadState }) {
  const {
    active,
    reply,
    setReply,
    selectedFiles,
    setSelectedFiles,
    isSending,
    handleSendFollowUp,
  } = thread;

  if (!active) {
    return null;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
    event.target.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {selectedFiles.length > 0 ? (
        <div className="mh-attachments-strip mh-thread-composer-bar">
          {selectedFiles.map((file, index) => {
            const isImage = file.type.startsWith('image/');
            return (
              <div key={`${file.name}-${index}`} className="mh-attachment-chip">
                {isImage ? (
                  <img src={URL.createObjectURL(file)} alt={file.name} />
                ) : (
                  <FileText size={28} className="my-2 text-slate-500" />
                )}
                <span className="truncate w-full text-center">{file.name}</span>
                <button
                  type="button"
                  className="absolute top-0.5 right-0.5 rounded bg-white/90 p-0.5 text-slate-600"
                  aria-label="Remove attachment"
                  onClick={() => removeFile(index)}
                >
                  <X size={12} />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
      <div className="mh-composer mh-thread-composer-bar">
        <label htmlFor="mh-question-thread-attachment" className="mh-composer-attach">
          <Paperclip className="h-5 w-5" aria-hidden />
          <input
            title="Attach file"
            id="mh-question-thread-attachment"
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </label>
        <input
          type="text"
          className="mh-composer-input"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Reply in this thread…"
          disabled={isSending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSendFollowUp();
            }
          }}
        />
        <button
          type="button"
          className="mh-composer-send"
          onClick={() => void handleSendFollowUp()}
          disabled={isSending || (!reply.trim() && selectedFiles.length === 0)}
        >
          <Send className="inline h-4 w-4" aria-hidden />
          {isSending ? '…' : 'Send'}
        </button>
      </div>
    </>
  );
}

export type { QuestionThreadSource } from './useQuestionThread';
